import React, { useState, useEffect, useCallback } from 'react';
import { X, MessageSquare } from 'lucide-react';
import { getCurrentUser } from '../services/auth';
import { neonQuery } from '../services/api';
import VipBadge from './VipBadge';
import organicFetch from '../lib/organicFetch';

// "Rebecca Bent" -> "RB" — the public signature appended to Page replies so
// customers (and the team) can see WHO answered as CrabPlace.com.
function staffInitials() {
  try {
    const u = getCurrentUser();
    const name = (u && (u.staffName || u.name)) || '';
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '';
    return parts.map(p => p[0].toUpperCase()).slice(0, 3).join('');
  } catch { return ''; }
}

// Shared Facebook-post modals, extracted from OrganicBuilder so the unified
// Performance tab can reuse them. Behavior is unchanged from the original
// OrganicBuilder implementations.

const inputStyle = { width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid var(--gray-200)', borderRadius: 6, fontFamily: 'Montserrat', color: 'var(--navy)', boxSizing: 'border-box' };
const primaryBtn = { background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'Montserrat' };
const ghostBtn = { background: '#fff', color: 'var(--navy)', border: '1px solid var(--gray-200)', borderRadius: 6, fontWeight: 600, fontSize: 12, cursor: 'pointer', fontFamily: 'Montserrat', padding: '6px 12px' };

export function fmtViews(n) {
  const v = parseInt(n || 0, 10);
  if (v >= 1e6) return (v / 1e6).toFixed(v >= 1e7 ? 0 : 1) + 'M';
  if (v >= 1e3) return (v / 1e3).toFixed(v >= 1e4 ? 0 : 1) + 'K';
  return v.toLocaleString();
}

// Drill-in: the people/sessions that moved through a post's funnel, keyed by
// cp_sid. Identity (name/email/returning) fills in once a session signs up or —
// after George's checkout deploy — enters their email at checkout.
export function FunnelPeopleModal({ data, onClose }) {
  const money = (n) => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtTime = (t) => { if (!t) return ''; try { return new Date(t).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch { return ''; } };
  const stepLabel = { checkout: 'Reached checkout', cart: 'Added to cart', signup: 'Newsletter signup', visit: 'Visited' };
  const stepColor = { checkout: '#1a8a4a', cart: 'var(--navy)', signup: 'var(--orange)', visit: 'var(--gray-500)' };
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '40px 16px' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, maxWidth: 620, width: '100%', boxShadow: '0 12px 40px rgba(0,0,0,.25)' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--gray-200)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 14 }}>People in this funnel</div>
            <div style={{ fontSize: 11.5, color: 'var(--gray-500)', marginTop: 2, maxWidth: 460 }}>{(data.caption || '').slice(0, 90)}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, lineHeight: 1, cursor: 'pointer', color: 'var(--gray-500)' }}>×</button>
        </div>
        <div style={{ padding: 14, maxHeight: '70vh', overflowY: 'auto' }}>
          {data.loading && <div style={{ fontSize: 12, color: 'var(--gray-500)', padding: 8 }}>Loading…</div>}
          {!data.loading && data.list.length === 0 && (
            <div style={{ fontSize: 12, color: 'var(--gray-500)', padding: 8, lineHeight: 1.6 }}>
              No identified sessions yet for this post. Sessions appear here as people move through cart and checkout. Names &amp; emails fill in once a visitor signs up for the newsletter or enters their email at checkout (the checkout capture goes live with the next storefront deploy).
            </div>
          )}
          {!data.loading && data.list.map((pp) => (
            <div key={pp.sid} style={{ border: '1px solid var(--gray-200)', borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--navy)' }}>
                  {pp.name || pp.email || <span style={{ color: 'var(--gray-500)', fontWeight: 500 }}>Anonymous session</span>}
                  {pp.returning != null && (
                    <span style={{ marginLeft: 8, fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: pp.returning ? '#e9f8ef' : '#fff4e8', color: pp.returning ? '#1a8a4a' : '#9a5b00' }}>{pp.returning ? `Returning · ${pp.pastOrders} orders` : 'New customer'}</span>
                  )}
                </div>
                <span style={{ fontSize: 10.5, fontWeight: 700, color: stepColor[pp.step] }}>{stepLabel[pp.step]}</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 5, display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {pp.email && <span>✉ {pp.email}</span>}
                {pp.addedToCart && <span>🛒 {pp.itemNames && pp.itemNames.length ? pp.itemNames.join(', ') : `${pp.items || 0} item${pp.items === 1 ? '' : 's'}`}{pp.cartValue ? ` · ${money(pp.cartValue)}` : ''}</span>}
                {pp.lifetimeValue != null && pp.lifetimeValue > 0 && <span>💰 {money(pp.lifetimeValue)} lifetime</span>}
                {pp.lastAt && <span>🕒 {fmtTime(pp.lastAt)}</span>}
              </div>
              {pp.journey && (
                <div style={{ fontSize: 11, color: 'var(--navy)', marginTop: 6, paddingTop: 6, borderTop: '1px dashed var(--gray-200)', display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  {pp.journey.account && <span>{pp.journey.account === 'guest' ? '👤 Guest' : `🔑 ${pp.journey.account}`}</span>}
                  {pp.journey.signinFailed && <span style={{ color: '#c0392b' }}>⚠ sign-in failed</span>}
                  {pp.journey.hasDeliveryDate ? <span>📅 Picked a delivery date</span> : (pp.journey.usedCalendar && <span style={{ color: 'var(--gray-500)' }}>📅 No date picked</span>)}
                  {pp.journey.zip && <span>📍 {pp.journey.zip}</span>}
                  {pp.journey.vipMember && <span style={{ color: 'var(--orange)' }}>★ VIP</span>}
                  {pp.journey.furthestStep && <span style={{ color: 'var(--gray-500)' }}>↦ got to {pp.journey.furthestStep.replace('_', ' ')}</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Checkout step funnel: click a post's Checkout number to see how far those
// sessions got toward an order (address -> delivery/shipping -> card -> placed).
// Self-fetches on open. Sessions are the ones we could trace into the checkout
// app via the post's UTM tag.
export function CheckoutStepsModal({ data, onClose }) {
  const [state, setState] = useState('loading'); // loading | ok | error
  const [steps, setSteps] = useState([]);
  const [traced, setTraced] = useState(0);
  const [placed, setPlaced] = useState(null); // { total, valid, revenue, internal }
  useEffect(() => {
    let alive = true;
    organicFetch({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'checkout-steps', utmContent: data.utmContent }) })
      .then(r => r.json())
      .then(d => { if (!alive) return; setSteps(Array.isArray(d.steps) ? d.steps : []); setTraced(d.traced || 0); setPlaced(d.placed || null); setState('ok'); })
      .catch(() => { if (alive) setState('error'); });
    return () => { alive = false; };
  }, [data.utmContent]);
  const money = (n) => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const top = steps.length ? (steps[0].count || 0) : 0;
  const barColor = ['#1877F2', '#2f6fd0', '#3a7f5a', '#c8781f', '#1a8a4a'];
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '40px 16px' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, maxWidth: 560, width: '100%', boxShadow: '0 12px 40px rgba(0,0,0,.25)' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--gray-200)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 14 }}>How close they got to an order</div>
            <div style={{ fontSize: 11.5, color: 'var(--gray-500)', marginTop: 2, maxWidth: 440 }}>{(data.caption || '').slice(0, 90)}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, lineHeight: 1, cursor: 'pointer', color: 'var(--gray-500)' }}>×</button>
        </div>
        <div style={{ padding: 18 }}>
          {state === 'loading' && <div style={{ fontSize: 12, color: 'var(--gray-500)', padding: 8 }}>Loading the checkout steps…</div>}
          {state === 'error' && <div style={{ fontSize: 12, color: '#c33', padding: 8 }}>Couldn't load the step breakdown right now.</div>}
          {state === 'ok' && traced === 0 && (
            <div style={{ fontSize: 12.5, color: 'var(--gray-500)', padding: 8, lineHeight: 1.6 }}>
              We couldn't trace step-by-step checkout data for this post yet. It fills in once a visitor from this post's link opens the checkout page (the checkout app reports each step it reaches).
            </div>
          )}
          {state === 'ok' && traced > 0 && (
            <>
              <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 12, lineHeight: 1.5 }}>
                {data.checkouts && data.checkouts > top ? (
                  <><strong style={{ color: 'var(--navy)' }}>{data.checkouts.toLocaleString()}</strong> sessions reached checkout from this post. We can follow <strong style={{ color: 'var(--navy)' }}>{top.toLocaleString()}</strong> of them step-by-step (the rest lost the post's tag crossing from the store into the checkout app). Of those {top.toLocaleString()}:</>
                ) : (
                  <>Of the <strong style={{ color: 'var(--navy)' }}>{top.toLocaleString()}</strong> sessions from this post that opened checkout, here's the furthest step each reached:</>
                )}
              </div>
              {steps.map((s, i) => {
                const pct = top ? Math.round((s.count / top) * 100) : 0;
                const dropped = i > 0 ? (steps[i - 1].count - s.count) : 0;
                return (
                  <div key={s.key} style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                      <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--navy)' }}>{s.label}</span>
                      <span style={{ fontSize: 12, color: 'var(--gray-500)' }}><strong style={{ color: 'var(--navy)' }}>{s.count.toLocaleString()}</strong> · {pct}%</span>
                    </div>
                    <div style={{ height: 16, background: 'var(--gray-100)', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: barColor[i] || 'var(--navy)', borderRadius: 4, transition: 'width .3s' }} />
                    </div>
                    {dropped > 0 && <div style={{ fontSize: 10.5, color: '#c0623a', marginTop: 3, textAlign: 'right' }}>↓ {dropped.toLocaleString()} dropped off here</div>}
                    {s.key === 'order' && placed && placed.total > 0 && (
                      <div style={{ fontSize: 11, marginTop: 4, textAlign: 'right', color: placed.valid > 0 ? '#1a8a4a' : 'var(--gray-500)', fontWeight: 600 }}>
                        {placed.valid > 0
                          ? `${placed.valid} real order${placed.valid === 1 ? '' : 's'} · ${money(placed.revenue)}`
                          : 'no real orders yet'}
                        {placed.internal > 0 && <span style={{ color: 'var(--gray-400)', fontWeight: 400 }}> · {placed.internal} internal test{placed.internal === 1 ? '' : 's'}</span>}
                      </div>
                    )}
                  </div>
                );
              })}
              <div style={{ fontSize: 10.5, color: 'var(--gray-400)', marginTop: 10, lineHeight: 1.5, borderTop: '1px dashed var(--gray-200)', paddingTop: 10 }}>
                "Loaded checkout" comes from the checkout app's own signal (linked by session id), so it can differ from the Checkout column, which uses the store's begin-checkout tag. "Real orders" excludes canceled/deleted orders and our own @crabplace.com test orders.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Overlay that embeds the live Facebook post (image, caption, reactions) via the
// FB embedded-post plugin, with the row's tracked stats alongside.
// `adTabs` (optional, paid-ads use): { ads: [{id, name, ...}], activeId, onSelect }
// — renders a pill row to flip between a campaign's ads without leaving the modal.
export function PostPreviewModal({ post, onClose, adTabs }) {
  const embed = post.permalink
    ? (post.platform === 'instagram'
        ? `${post.permalink.replace(/\/?$/, '/')}embed`
        : `https://www.facebook.com/plugins/post.php?href=${encodeURIComponent(post.permalink)}&show_text=true&width=500`)
    : null;
  const [comments, setComments] = useState(null);
  const [cState, setCState] = useState('loading');
  const loadComments = useCallback((silent) => {
    if (!silent) { setCState('loading'); setComments(null); }
    organicFetch({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(post.platform === 'instagram' ? { action: 'ig-media-comments', mediaId: post.fbPostId, brand: post.brand } : { action: 'post-comments', fbPostId: post.fbPostId, brand: post.brand }) })
      .then(r => r.json())
      .then(d => { setComments(d.comments || []); setCState((d.comments || []).length || !d.error ? 'done' : 'error'); })
      .catch(() => setCState('error'));
  }, [post.fbPostId, post.brand]);
  useEffect(() => { loadComments(); }, [loadComments]);
  const cTime = (t) => { try { return new Date(t).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch (e) { return ''; } };
  // Reply (posts as the CrabPlace.com Page).
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyBusy, setReplyBusy] = useState(false);
  const [replyErr, setReplyErr] = useState(null);
  const [replied, setReplied] = useState({});
  const [hideMap, setHideMap] = useState({});   // comment id -> hidden override
  const [hideBusy, setHideBusy] = useState(null); // comment id being toggled
  const [hideErr, setHideErr] = useState(null);
  const [deleted, setDeleted] = useState({});     // comment id -> true after delete
  const [modBusy, setModBusy] = useState(null);   // comment id mid delete/block
  const [blocked, setBlocked] = useState({});     // from-user id -> true after block
  // Customer-record match: comment id -> { state: 'loading'|'done'|'none'|'error', rows }
  const [custMatch, setCustMatch] = useState({});
  // Comment -> customer-record attachment: "<commentId>:<customerId>" -> 'busy'|'done'
  const [attached, setAttached] = useState({});
  async function attachToCustomer(c, cust) {
    const key = `${c.id}:${cust.id}`;
    setAttached(p => ({ ...p, [key]: 'busy' }));
    try {
      let staffName = '';
      try { const u = getCurrentUser(); staffName = (u && (u.staffName || u.name)) || ''; } catch (e) { /* noop */ }
      await neonQuery('social_note_add', {
        customerId: cust.id,
        platform: post.platform === 'instagram' ? 'instagram' : 'facebook',
        commentId: c.id,
        commentText: c.message,
        commenterName: c.fromName || null,
        postCaption: post.caption || null,
        permalink: post.permalink || null,
        attachedBy: staffName || null,
      });
      setAttached(p => ({ ...p, [key]: 'done' }));
    } catch (e) {
      setAttached(p => ({ ...p, [key]: undefined }));
      setHideErr(`Could not attach: ${e.message}`);
    }
  }
  // AI reply suggestions for the comment currently being replied to.
  const [suggest, setSuggest] = useState(null); // { state: 'loading'|'done'|'error', options, error }
  async function suggestReplies(c) {
    setSuggest({ state: 'loading', options: [] });
    // If a customer match already ran, hand its top hit to the AI as context
    // (order count, VIP, lifetime value) so a complaint reply can be smarter.
    const match = custMatch[c.id];
    const top = match && match.state === 'done' && match.rows[0];
    const customerContext = top
      ? `${[top.first_name, top.last_name].filter(Boolean).join(' ')}, ${top.order_count || 0} orders, $${Math.round(Number(top.lifetime_value) || 0)} lifetime${Number(top.vip_status) ? ', VIP member' : ''} (name-matched, unverified)`
      : '';
    try {
      const r = await organicFetch({
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'suggest-reply',
          brand: post.brand,
          commentText: c.message,
          commenterName: (c.fromName && !/^(Facebook|Instagram) user$/.test(c.fromName)) ? c.fromName : '',
          postCaption: post.caption || '',
          priorReplies: (c.replies || []).map(x => x.message).filter(Boolean),
          customerContext,
        }),
      }).then(x => x.json());
      if (r.suggestions && r.suggestions.length) setSuggest({ state: 'done', options: r.suggestions });
      else setSuggest({ state: 'error', options: [], error: r.error || 'No suggestions came back.' });
    } catch (e) { setSuggest({ state: 'error', options: [], error: e.message }); }
  }
  const initials = staffInitials();
  // Instagram posts use the ig-* backend actions (same response shapes). IG has
  // no block-user API, and usernames come back reliably (fromName = @handle).
  const isIG = post.platform === 'instagram';
  const ACT = isIG
    ? { comments: 'ig-media-comments', reply: 'ig-comment-reply', hide: 'ig-comment-hide', del: 'ig-comment-delete' }
    : { comments: 'post-comments', reply: 'comment-reply', hide: 'comment-hide', del: 'comment-delete' };
  const netName = isIG ? 'Instagram' : 'Facebook';
  const isHidden = (c) => (hideMap[c.id] !== undefined ? hideMap[c.id] : !!c.hidden);
  async function deleteComment(c) {
    if (!window.confirm(`Delete this comment from ${netName}? This is permanent (Hide is reversible, Delete is not).`)) return;
    setModBusy(c.id); setHideErr(null);
    try {
      const r = await organicFetch({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: ACT.del, commentId: c.id, brand: post.brand }) }).then(x => x.json());
      if (r.ok) setDeleted(p => ({ ...p, [c.id]: true }));
      else setHideErr(r.error || 'Could not delete that comment.');
    } catch (e) { setHideErr(e.message); }
    finally { setModBusy(null); }
  }
  async function blockUser(c) {
    const who = c.fromName && c.fromName !== 'Facebook user' ? c.fromName : 'this person';
    if (!window.confirm(`Block ${who} from the Page? They can no longer comment on or message the Page, and Facebook hides their existing comments.`)) return;
    setModBusy(c.id); setHideErr(null);
    try {
      const r = await organicFetch({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'user-block', userId: c.fromId, brand: post.brand }) }).then(x => x.json());
      if (r.ok) setBlocked(p => ({ ...p, [c.fromId]: true }));
      else setHideErr(r.error || 'Could not block that user.');
    } catch (e) { setHideErr(e.message); }
    finally { setModBusy(null); }
  }
  // Try to tie the commenter to an in-house customer record by name.
  const isGenericName = (n) => !n || /^(Facebook|Instagram) user$/.test(n);
  // Handle memory: normalized handle/name -> linked customer (from prior 📌 attaches)
  const [known, setKnown] = useState({});
  const normHandle = (n) => String(n || '').trim().replace(/^@/, '').toLowerCase();
  useEffect(() => {
    if (!comments || !comments.length) return;
    const names = [...new Set(comments.map(c => c.fromName).filter(n => !isGenericName(n)).map(normHandle))];
    if (!names.length) return;
    neonQuery('social_handles_lookup', { platform: isIG ? 'instagram' : 'facebook', handles: names })
      .then(rows => {
        const m = {};
        (rows || []).forEach(r => { m[r.handle] = r; });
        setKnown(m);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comments]);
  // Manual customer search (for IG's anonymous "Instagram user" comments, or to
  // refine a bad name match) — one open search box at a time.
  const [findFor, setFindFor] = useState(null); // comment id with the search box open
  const [findQ, setFindQ] = useState('');
  function openFind(c) {
    setFindFor(c.id);
    const seed = isGenericName(c.fromName) ? '' : c.fromName.replace(/^@/, '');
    setFindQ(seed);
    if (seed) matchCustomer(c, seed);
    else setCustMatch(p => { const n = { ...p }; delete n[c.id]; return n; });
  }
  async function matchCustomer(c, q) {
    setCustMatch(p => ({ ...p, [c.id]: { state: 'loading', rows: [] } }));
    try {
      const rows = await neonQuery('customer_search', { q: q || c.fromName });
      const top = (rows || []).slice(0, 3);
      setCustMatch(p => ({ ...p, [c.id]: { state: top.length ? 'done' : 'none', rows: top } }));
    } catch (e) {
      setCustMatch(p => ({ ...p, [c.id]: { state: 'error', rows: [] } }));
    }
  }
  async function toggleHide(c) {
    const next = !isHidden(c);
    setHideBusy(c.id); setHideErr(null);
    try {
      const r = await organicFetch({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: ACT.hide, commentId: c.id, hide: next, brand: post.brand }) }).then(x => x.json());
      if (r.ok) setHideMap(p => ({ ...p, [c.id]: next }));
      else setHideErr(r.error || 'Could not hide that comment.');
    } catch (e) { setHideErr(e.message); }
    finally { setHideBusy(null); }
  }
  async function sendReply(commentId) {
    if (!replyText.trim() || replyBusy) return;
    setReplyBusy(true); setReplyErr(null);
    try {
      // Sign the reply with the logged-in user's initials so customers (and the
      // team) can see who answered as the Page: "... - RB"
      const signed = replyText.trim() + (initials ? ` - ${initials}` : '');
      const r = await organicFetch({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: ACT.reply, commentId, message: signed, brand: post.brand }) }).then(x => x.json());
      if (r.ok) { setReplied(p => ({ ...p, [commentId]: true })); setReplyTo(null); setReplyText(''); setTimeout(() => loadComments(true), 1200); }
      else setReplyErr(r.error || 'Could not reply — the Page may need comment-management permission.');
    } catch (e) { setReplyErr(e.message); }
    finally { setReplyBusy(false); }
  }
  const stat = (label, val) => (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)' }}>{val}</div>
      <div style={{ fontSize: 10, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</div>
    </div>
  );
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, width: '100%', maxWidth: 560, maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--gray-200)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <strong style={{ fontSize: 13, color: 'var(--navy)' }}>{adTabs ? 'Ad preview' : 'Post preview'}</strong>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {post.linkUrl && <a href={post.linkUrl} target="_blank" rel="noreferrer" style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)' }} title={post.linkUrl}>Landing page ↗</a>}
            {post.permalink && <a href={post.permalink} target="_blank" rel="noreferrer" style={{ fontSize: 11, fontWeight: 600, color: 'var(--orange)' }}>{`Open on ${post.platform === 'instagram' ? 'Instagram' : 'Facebook'}`} ↗</a>}
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><X size={18} color="var(--gray-500)" /></button>
          </div>
        </div>
        {adTabs && adTabs.ads && adTabs.ads.length > 1 && (
          <div style={{ display: 'flex', gap: 6, padding: '8px 16px', borderBottom: '1px solid var(--gray-100)', overflowX: 'auto', justifyContent: 'flex-start' }}>
            {adTabs.ads.map((a) => (
              <button key={a.id} onClick={() => adTabs.onSelect(a)} title={a.name}
                style={{
                  flex: '0 0 auto', maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  padding: '4px 11px', fontSize: 11, fontWeight: 600, borderRadius: 999, cursor: 'pointer', fontFamily: 'Montserrat',
                  background: a.id === adTabs.activeId ? 'var(--navy)' : '#fff',
                  color: a.id === adTabs.activeId ? '#fff' : 'var(--navy)',
                  border: `1px solid ${a.id === adTabs.activeId ? 'var(--navy)' : 'var(--gray-200)'}`,
                }}>
                {a.name}
              </button>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-around', padding: '10px 16px', borderBottom: '1px solid var(--gray-100)', background: 'var(--gray-50)' }}>
          {stat('Likes', (post.likes || 0).toLocaleString())}
          {stat('Comments', (post.comments || 0).toLocaleString())}
          {post.views ? stat('Views', fmtViews(post.views)) : stat('Shares', (post.shares || 0).toLocaleString())}
          {stat('Orders', post.orders || 0)}
        </div>
        <div style={{ flex: 1, overflowY: 'auto', background: '#f0f2f5' }}>
          <div style={{ display: 'flex', justifyContent: 'center', padding: 12 }}>
            {embed ? (
              <iframe title="Facebook post" src={embed} style={{ width: 500, height: 560, border: 'none', overflow: 'hidden', background: '#fff', borderRadius: 8 }} scrolling="no" allow="encrypted-media" />
            ) : (
              <div style={{ textAlign: 'center', color: 'var(--gray-500)', fontSize: 12, padding: 24 }}>
                {post.thumb && <img src={post.thumb} alt="" style={{ maxWidth: 360, borderRadius: 8, marginBottom: 12 }} />}
                <div style={{ color: 'var(--navy)', fontSize: 13, marginBottom: 8 }}>{post.caption || '(no caption)'}</div>
                <div>The live post preview isn't available for this one yet (it may still be processing on Facebook).</div>
              </div>
            )}
          </div>
          {/* Comments (read via the Graph API — the embed only shows the count) */}
          <div style={{ background: '#fff', borderTop: '1px solid var(--gray-200)', padding: '12px 16px' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy)', marginBottom: 10 }}>
              Comments{comments ? ` (${comments.length})` : ''}
            </div>
            {hideErr && <div style={{ fontSize: 10.5, color: '#c33', marginBottom: 8 }}>{hideErr}</div>}
            {cState === 'loading' ? (
              <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>Loading comments…</div>
            ) : cState === 'error' ? (
              <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>Couldn't load comments for this post.</div>
            ) : comments && comments.length ? (
              comments.filter(c => !deleted[c.id]).map(c => (
                <div key={c.id} style={{ display: 'flex', gap: 8, padding: '8px 0', borderBottom: '1px solid var(--gray-100)', opacity: (isHidden(c) || blocked[c.fromId]) ? 0.55 : 1 }}>
                  <MessageSquare size={14} style={{ color: 'var(--gray-300)', flexShrink: 0, marginTop: 3 }} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 12.5, color: 'var(--navy)' }}>
                      {!isGenericName(c.fromName) && <strong style={{ marginRight: 6 }}>{c.fromName}</strong>}
                      {known[normHandle(c.fromName)] && (
                        <span title="This handle was previously linked to a customer record"
                          style={{ fontSize: 9.5, fontWeight: 700, color: '#1a6fb0', background: '#e8f4fd', borderRadius: 3, padding: '1px 6px', marginRight: 6 }}>
                          ↺ {[known[normHandle(c.fromName)].first_name, known[normHandle(c.fromName)].last_name].filter(Boolean).join(' ') || known[normHandle(c.fromName)].email}
                        </span>
                      )}
                      {isHidden(c) && <span style={{ fontSize: 9.5, fontWeight: 700, color: '#9a5b00', background: '#fff3e0', borderRadius: 3, padding: '1px 5px', marginRight: 6, textTransform: 'uppercase', letterSpacing: 0.3 }}>Hidden</span>}
                      {blocked[c.fromId] && <span style={{ fontSize: 9.5, fontWeight: 700, color: '#a12626', background: '#fdecec', borderRadius: 3, padding: '1px 5px', marginRight: 6, textTransform: 'uppercase', letterSpacing: 0.3 }}>Blocked</span>}
                      {c.message || <em style={{ color: 'var(--gray-400)' }}>(reaction, no text)</em>}
                    </div>
                    <div style={{ fontSize: 10.5, color: 'var(--gray-500)', marginTop: 2, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span>{cTime(c.createdTime)}{c.likeCount ? ` · ${c.likeCount} like${c.likeCount === 1 ? '' : 's'}` : ''}{c.replyCount ? ` · ${c.replyCount} repl${c.replyCount === 1 ? 'y' : 'ies'}` : ''}</span>
                      {replied[c.id]
                        ? <span style={{ color: '#1a8a4a', fontWeight: 600 }}>Replied ✓</span>
                        : <button onClick={() => { setReplyTo(replyTo === c.id ? null : c.id); setReplyText(''); setReplyErr(null); setSuggest(null); }} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--orange)', cursor: 'pointer', fontWeight: 600, fontSize: 10.5 }}>Reply</button>}
                      <button onClick={() => toggleHide(c)} disabled={hideBusy === c.id} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--gray-500)', cursor: 'pointer', fontWeight: 600, fontSize: 10.5 }}>
                        {hideBusy === c.id ? '…' : isHidden(c) ? 'Unhide' : 'Hide'}
                      </button>
                      <button onClick={() => deleteComment(c)} disabled={modBusy === c.id} title="Delete this comment from Facebook (permanent)"
                        style={{ background: 'none', border: 'none', padding: 0, color: '#c0392b', cursor: 'pointer', fontWeight: 600, fontSize: 10.5 }}>
                        {modBusy === c.id ? '…' : 'Delete'}
                      </button>
                      {c.fromId && !blocked[c.fromId] && (
                        <button onClick={() => blockUser(c)} disabled={modBusy === c.id} title="Block this person from commenting on or messaging the Page"
                          style={{ background: 'none', border: 'none', padding: 0, color: '#c0392b', cursor: 'pointer', fontWeight: 600, fontSize: 10.5 }}>
                          Block user
                        </button>
                      )}
                      {known[normHandle(c.fromName)] && !attached[`${c.id}:${known[normHandle(c.fromName)].customer_id}`] && (
                        <button onClick={() => attachToCustomer(c, { id: known[normHandle(c.fromName)].customer_id })}
                          title="Attach this comment to the previously linked customer"
                          style={{ background: 'none', border: 'none', padding: 0, color: '#1a6fb0', cursor: 'pointer', fontWeight: 600, fontSize: 10.5 }}>
                          📌 Attach to {known[normHandle(c.fromName)].first_name || 'linked customer'}
                        </button>
                      )}
                      {attached[`${c.id}:${(known[normHandle(c.fromName)] || {}).customer_id}`] === 'done' && (
                        <span style={{ color: '#1a8a4a', fontWeight: 600, fontSize: 10.5 }}>Attached ✓</span>
                      )}
                      {findFor !== c.id && (
                        <button onClick={() => openFind(c)} title="Search our customer records (type a name, email, or phone if the platform hides the commenter's name)"
                          style={{ background: 'none', border: 'none', padding: 0, color: 'var(--gray-500)', cursor: 'pointer', fontWeight: 600, fontSize: 10.5 }}>
                          Find customer
                        </button>
                      )}
                    </div>
                    {findFor === c.id && (
                      <div style={{ marginTop: 5, display: 'flex', gap: 6, alignItems: 'center' }}>
                        <input value={findQ} onChange={e => setFindQ(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter' && findQ.trim()) matchCustomer(c, findQ.trim()); }}
                          placeholder="Customer name, email, or phone…"
                          style={{ flex: 1, padding: '5px 8px', fontSize: 11.5, border: '1px solid var(--gray-200)', borderRadius: 5, fontFamily: 'Montserrat', color: 'var(--navy)' }} />
                        <button type="button" onClick={() => findQ.trim() && matchCustomer(c, findQ.trim())}
                          style={{ background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 5, padding: '5px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'Montserrat' }}>Search</button>
                        <button type="button" onClick={() => { setFindFor(null); setCustMatch(p => { const n = { ...p }; delete n[c.id]; return n; }); }}
                          style={{ background: 'none', border: 'none', color: 'var(--gray-500)', cursor: 'pointer', fontSize: 12 }}>✕</button>
                      </div>
                    )}
                    {custMatch[c.id] && (
                      <div style={{ marginTop: 5, padding: '6px 8px', background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: 6, fontSize: 11 }}>
                        {custMatch[c.id].state === 'loading' && <span style={{ color: 'var(--gray-500)' }}>Searching customer records…</span>}
                        {custMatch[c.id].state === 'none' && <span style={{ color: 'var(--gray-500)' }}>No customer record matches "{c.fromName}". (Facebook names often differ from order names.)</span>}
                        {custMatch[c.id].state === 'error' && <span style={{ color: '#c33' }}>Customer lookup failed.</span>}
                        {custMatch[c.id].state === 'done' && custMatch[c.id].rows.map(r => {
                          const aKey = `${c.id}:${r.id}`;
                          const aState = attached[aKey];
                          return (
                            <div key={r.id} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '2px 0', color: 'var(--navy)' }}>
                              <strong>{[r.first_name, r.last_name].filter(Boolean).join(' ')}</strong>
                              {Number(r.vip_status) ? <VipBadge notRenewing={Number(r.vip_not_renewing) > 0} style={{ fontSize: 9, fontWeight: 800, color: '#7a5b00', background: 'linear-gradient(180deg,#fce5b3,#dcb164)', borderRadius: 3, padding: '1px 5px' }} /> : null}
                              <span style={{ color: 'var(--gray-500)' }}>{r.email}</span>
                              <span style={{ color: 'var(--gray-500)' }}>{r.order_count || 0} orders{Number(r.lifetime_value) > 0 ? ` · $${Number(r.lifetime_value).toLocaleString('en-US', { maximumFractionDigits: 0 })} lifetime` : ''}</span>
                              {aState === 'done'
                                ? <span style={{ color: '#1a8a4a', fontWeight: 700, fontSize: 10.5 }}>✓ Attached to record</span>
                                : <button type="button" onClick={() => attachToCustomer(c, r)} disabled={aState === 'busy'}
                                    title="Save this comment onto the customer's record so CS sees it when they call"
                                    style={{ background: 'none', border: '1px solid var(--gray-200)', borderRadius: 4, padding: '1px 8px', color: 'var(--navy)', cursor: 'pointer', fontWeight: 600, fontSize: 10.5, fontFamily: 'Montserrat' }}>
                                    {aState === 'busy' ? 'Attaching…' : '📌 Attach comment'}
                                  </button>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {replyTo === c.id && (
                      <div style={{ marginTop: 6 }}>
                        <textarea value={replyText} onChange={e => setReplyText(e.target.value)} rows={2} placeholder="Reply as CrabPlace.com…" style={{ ...inputStyle, fontSize: 12, resize: 'vertical' }} />
                        {initials && <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 2 }}>Posts publicly as <strong>CrabPlace.com</strong>, signed with your initials: “… - {initials}”</div>}
                        {replyErr && <div style={{ fontSize: 10.5, color: '#c33', marginTop: 3 }}>{replyErr}</div>}
                        {suggest && suggest.state === 'loading' && (
                          <div style={{ fontSize: 11, color: '#7c3aed', marginTop: 6 }}>✨ Drafting suggestions from the knowledge base…</div>
                        )}
                        {suggest && suggest.state === 'error' && (
                          <div style={{ fontSize: 10.5, color: '#c33', marginTop: 4 }}>{suggest.error}</div>
                        )}
                        {suggest && suggest.state === 'done' && (
                          <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 5 }}>
                            {suggest.options.map((o, i) => (
                              <button key={i} type="button" onClick={() => setReplyText(o.text)}
                                title="Click to load into the reply box — edit before sending"
                                style={{ textAlign: 'left', background: '#faf7ff', border: '1px solid #e7dbff', borderRadius: 6, padding: '7px 9px', cursor: 'pointer', fontFamily: 'Montserrat' }}>
                                <div style={{ fontSize: 9.5, fontWeight: 800, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 2 }}>{o.label}</div>
                                <div style={{ fontSize: 11.5, color: 'var(--navy)', lineHeight: 1.45 }}>{o.text}</div>
                              </button>
                            ))}
                            <div style={{ fontSize: 9.5, color: 'var(--gray-400)' }}>Click one to load it into the box, edit as needed, then Send. Nothing posts without you.</div>
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                          <button onClick={() => sendReply(c.id)} disabled={replyBusy || !replyText.trim()} style={{ ...primaryBtn, padding: '4px 12px', fontSize: 11, opacity: (replyBusy || !replyText.trim()) ? 0.6 : 1 }}>{replyBusy ? 'Sending…' : 'Send reply'}</button>
                          <button type="button" onClick={() => suggestReplies(c)} disabled={suggest && suggest.state === 'loading'}
                            style={{ ...ghostBtn, padding: '4px 10px', fontSize: 11, color: '#7c3aed', borderColor: '#d9c7ff' }}>
                            ✨ Suggest replies
                          </button>
                          <button onClick={() => { setReplyTo(null); setReplyText(''); setSuggest(null); }} style={{ ...ghostBtn, padding: '4px 10px', fontSize: 11 }}>Cancel</button>
                        </div>
                      </div>
                    )}
                    {c.replies && c.replies.length > 0 && (
                      <div style={{ marginTop: 6, paddingLeft: 10, borderLeft: '2px solid var(--gray-100)' }}>
                        {c.replies.map(r => (
                          <div key={r.id} style={{ padding: '4px 0', opacity: r.hidden ? 0.55 : 1 }}>
                            <div style={{ fontSize: 12, color: 'var(--navy)' }}>
                              {r.hidden && <span style={{ fontSize: 9, fontWeight: 700, color: '#9a5b00', background: '#fff3e0', borderRadius: 3, padding: '1px 5px', marginRight: 5, textTransform: 'uppercase' }}>Hidden</span>}
                              {r.fromName !== 'Facebook user'
                                ? <strong style={{ color: 'var(--orange)' }}>{r.fromName} </strong>
                                : <MessageSquare size={11} style={{ color: 'var(--gray-300)', verticalAlign: 'middle', marginRight: 4 }} />}
                              {r.message}
                            </div>
                            <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 1 }}>{cTime(r.createdTime)}{r.likeCount ? ` · ${r.likeCount} like${r.likeCount === 1 ? '' : 's'}` : ''}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>No comments on this post yet.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
